// A card is painted at least this tall so a very short service call stays readable. The floor has to
// live here rather than as a CSS `min-height`: this function also decides which events collide, and a
// floor it cannot see would grow a card over a later one that the collision test judged clear.
// Mirrors EventCalendar.less, where an hour of grid is @height-timeslot-group tall, and
// AppointmentCard, which wraps one 20px line of footnote text in 2px of padding.
const GRID_HOUR_HEIGHT_PX = 90;
const CARD_MINIMUM_HEIGHT_PX = 24;
const MINIMUM_RENDERED_MS = (CARD_MINIMUM_HEIGHT_PX / GRID_HOUR_HEIGHT_PX) * 60 * 60 * 1000;

// Each stacked card is indented from the left so the one beneath it keeps an exposed strip to hover
// and click. The indent is a share of the column rather than a fixed pixel offset: .rbc-day-slot is
// `overflow: hidden`, so a fixed offset walks deep stacks straight out of the column, where they are
// invisible and every click lands on whatever spans the full width underneath. Levels share a fixed
// budget, so an eleventh card is thinner but still inside the column.
const INDENT_PER_LEVEL_PERCENT = 12;
const TOTAL_INDENT_BUDGET_PERCENT = 60;

// The end the card is actually drawn to. Only ever later than the real end, and never written back to
// the event, so the times the calendar reports stay the real ones.
const getRenderedEnd = (event, accessors) => {
	const start = accessors.start(event).getTime();
	const end = accessors.end(event).getTime();
	return new Date(Math.max(end, start + MINIMUM_RENDERED_MS));
};

const stackingLayout = ({ events, slotMetrics, accessors }) => {
	if (!events || events.length === 0) return [];

	const sorted = [...events].sort((a, b) => {
		const startDiff = accessors.start(a).getTime() - accessors.start(b).getTime();
		if (startDiff !== 0) return startDiff;
		return accessors.end(b).getTime() - accessors.end(a).getTime();
	});

	const stackIndices: Map<any, number> = new Map();

	sorted.forEach((event, index) => {
		const eventStart = accessors.start(event).getTime();
		let stackIndex = 0;

		for (let i = 0; i < index; i++) {
			const prior = sorted[i];
			if (getRenderedEnd(prior, accessors).getTime() > eventStart) {
				stackIndex = Math.max(stackIndex, (stackIndices.get(prior) || 0) + 1);
			}
		}

		stackIndices.set(event, stackIndex);
	});

	const deepestStack = Math.max(0, ...Array.from(stackIndices.values()));
	const indentPercent =
		deepestStack > 0
			? Math.min(INDENT_PER_LEVEL_PERCENT, TOTAL_INDENT_BUDGET_PERCENT / deepestStack)
			: 0;

	return sorted.map((event) => {
		const { top, height } = slotMetrics.getRange(
			accessors.start(event),
			getRenderedEnd(event, accessors)
		);
		const xOffset = (stackIndices.get(event) || 0) * indentPercent;

		return {
			event,
			style: {
				top,
				height,
				width: 100 - xOffset,
				xOffset,
			},
		};
	});
};

export default stackingLayout;
